// src/api/features/products/api.ts

import { request } from "@/api/client";
import { PATHS } from "@/api/constants";

import type {
  Product,
  ProductWithConfig,
  ProductListQuery,
  PaginatedProductItems,
  CreateProductPayload,
  UpdateProductPayload,
} from "./types";

/* ======================================================
 * Helpers
 * ====================================================== */

type ApiEnvelope<T> = { ok: boolean; message?: string; data: T; meta?: any };

function unwrap<T>(res: any): T {
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    return (res as ApiEnvelope<T>).data;
  }
  return res as T;
}

function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();

  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    u.set(k, String(v));
  }

  const s = u.toString();
  return s ? `?${s}` : "";
}

/* ======================================================
 * Base URL
 * ------------------------------------------------------
 * Backend route is /products → PATHS.products.base
 * ====================================================== */

const PRODUCTS_BASE = PATHS.products.base;
const PRODUCTS_PUBLIC = PATHS.products.public;

/* ======================================================
 * LIST – GET /products
 * Returns unwrapped pagination for FE: PaginatedProductItems
 * ====================================================== */

export async function listProducts(
  query: ProductListQuery = {}
): Promise<PaginatedProductItems> {
  const safePage = Math.max(1, Number(query.page ?? 1));
  const rawLimit = Number(query.limit ?? 10);
  const safeLimit = Math.max(
    1,
    Math.min(100, Number.isFinite(rawLimit) ? rawLimit : 10)
  );

  const url = `${PRODUCTS_BASE}${qs({
    page: safePage,
    limit: safeLimit,
    q: query.q,
    name: query.name?.trim() || undefined,
    slug: query.slug?.trim() || undefined,
  })}`;

  const res = await request<
    PaginatedProductItems | (ApiEnvelope<Product[]> & { meta?: any })
  >({
    method: "GET",
    url,
  });

  // If server returns { ok, data, meta }
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    const r = res as ApiEnvelope<Product[]> & { meta?: any };

    const items = Array.isArray(r.data) ? r.data : [];
    const meta = r.meta ?? {};

    return {
      items,
      page: Number(meta.page ?? safePage),
      pageSize: Number(meta.pageSize ?? meta.limit ?? safeLimit),
      total: Number(meta.total ?? items.length ?? 0),
    };
  }

  // Otherwise assume backend already returns PaginatedProductItems
  return res as PaginatedProductItems;
}

export async function listPublicProducts(
  query: ProductListQuery = {}
): Promise<PaginatedProductItems> {
  const safePage = Math.max(1, Number(query.page ?? 1));
  const rawLimit = Number(query.limit ?? 10);
  const safeLimit = Math.max(
    1,
    Math.min(100, Number.isFinite(rawLimit) ? rawLimit : 10)
  );

  const url = `${PRODUCTS_PUBLIC}${qs({
    page: safePage,
    limit: safeLimit,
    q: query.q,
  })}`;

  const res = await request<
    PaginatedProductItems | (ApiEnvelope<Product[]> & { meta?: any })
  >({
    method: "GET",
    url,
  });

  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    const r = res as ApiEnvelope<Product[]> & { meta?: any };

    const items = Array.isArray(r.data) ? r.data : [];
    const meta = r.meta ?? {};

    return {
      items,
      page: Number(meta.page ?? safePage),
      pageSize: Number(meta.pageSize ?? meta.limit ?? safeLimit),
      total: Number(meta.total ?? items.length ?? 0),
    };
  }

  return res as PaginatedProductItems;
}

/* ======================================================
 * DETAIL – GET /products/:id
 * Returns ProductWithConfig (product + tax rules + emails)
 * ====================================================== */

export async function getProduct(
  id: number | string
): Promise<ProductWithConfig> {
  const res = await request<ProductWithConfig | ApiEnvelope<ProductWithConfig>>(
    {
      method: "GET",
      url: `${PRODUCTS_BASE}/${id}`,
    }
  );

  return unwrap<ProductWithConfig>(res);
}

/* ======================================================
 * CREATE – POST /products
 * Body: CreateProductPayload
 * (includes markups, taxRules, emails)
 * Returns: ProductWithConfig
 * ====================================================== */

export async function createProduct(
  payload: CreateProductPayload
): Promise<ProductWithConfig> {
  const dto: any = { ...payload };

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[createProduct] payload →", dto);
  }

  const res = await request<
    ProductWithConfig | ApiEnvelope<ProductWithConfig>
  >({
    method: "POST",
    url: PRODUCTS_BASE,
    data: dto,
  });

  return unwrap<ProductWithConfig>(res);
}

/* ======================================================
 * UPDATE – PUT /products/:id
 * Body: UpdateProductPayload
 * (includes markups, taxRules, emails)
 * Returns: ProductWithConfig
 * ====================================================== */

export async function updateProduct(
  id: number | string,
  payload: UpdateProductPayload
): Promise<ProductWithConfig> {
  const dto: any = { ...payload };

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[updateProduct] id=", id, "payload →", dto);
  }

  const res = await request<
    ProductWithConfig | ApiEnvelope<ProductWithConfig>
  >({
    method: "PUT",
    url: `${PRODUCTS_BASE}/${id}`,
    data: dto,
  });

  return unwrap<ProductWithConfig>(res);
}

/* ======================================================
 * DELETE – DELETE /products/:id
 * Returns: simple message or nothing
 * ====================================================== */

export async function deleteProduct(
  id: number | string
): Promise<{ message: string } | void> {
  const res = await request<
    { message: string } | ApiEnvelope<{ message: string }>
  >({
    method: "DELETE",
    url: `${PRODUCTS_BASE}/${id}`,
  });

  // If API returns plain 204 No Content, res may be undefined
  if (!res) return;

  return unwrap<{ message: string }>(res);
}
